import { useState, useCallback, useRef, type ReactNode, type ChangeEvent } from 'react'
import { Search, X, Loader2 } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { Button } from '@/components/ui/button'
import { FilterSearchInput } from '@/components/shared/FilterSearchInput'
import {
  filterSearchBarIconButtonClassName,
  filterSearchBarIconButtonSurfaceClassName,
  filterSearchBarIconClassName,
  filterSearchBarShellClassName,
} from '@/components/shared/filterSheetStyles'

interface ArtistSearchBarProps {
  /** When set, notifies parent on every input change for local filtering. `onSearch` optional. */
  filterAsYouType?: boolean
  onQueryChange?: (query: string) => void
  placeholder?: string
  onSearch?: (query: string) => void
  onClear?: () => void
  isSearching?: boolean
  hasActiveSearch?: boolean
  /** Rendered to the left of the bordered search field with gap (e.g. map/list toggle). */
  startSlot?: ReactNode
}

export function ArtistSearchBar({
  filterAsYouType = false,
  onQueryChange,
  placeholder = 'Search artists by name, style...',
  onSearch,
  onClear,
  isSearching = false,
  hasActiveSearch = false,
  startSlot,
}: ArtistSearchBarProps) {
  const [query, setQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const dismissKeyboard = useCallback(() => {
    searchInputRef.current?.blur()
    if (Capacitor.isNativePlatform()) {
      void Keyboard.hide().catch(() => {})
    }
  }, [])

  const handleSearch = useCallback(() => {
    if (!query.trim()) return

    onSearch?.(query.trim())
    dismissKeyboard()
  }, [query, onSearch, dismissKeyboard])

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value
      setQuery(v)
      if (filterAsYouType) {
        onQueryChange?.(v)
      }
    },
    [filterAsYouType, onQueryChange]
  )

  const handleClearSearch = useCallback(() => {
    setQuery('')
    onClear?.()
    if (filterAsYouType) {
      onQueryChange?.('')
    }
  }, [onClear, filterAsYouType, onQueryChange])

  const isSearchDisabled = !query.trim() || isSearching
  const showClearButton = Boolean(
    !isSearching && (hasActiveSearch || query.trim().length > 0)
  )
  const idleSearchButtonSurface = `${filterSearchBarIconButtonSurfaceClassName} disabled:opacity-50`

  return (
    <div className={startSlot ? 'flex items-center gap-2' : undefined}>
      {startSlot}
      <div
        className={`${filterSearchBarShellClassName} ${startSlot ? 'min-w-0 flex-1' : ''}`}
      >
        <FilterSearchInput
          ref={searchInputRef}
          type="text"
          enterKeyHint="done"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (!isSearchDisabled) handleSearch()
          }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />

        <Button
          variant="ghost"
          size="icon"
          className={
            showClearButton
              ? `${filterSearchBarIconButtonClassName} rounded-r-lg disabled:opacity-50 ${filterSearchBarIconButtonSurfaceClassName}`
              : `${filterSearchBarIconButtonClassName} rounded-r-lg ${idleSearchButtonSurface}`
          }
          onClick={() => {
            if (showClearButton) handleClearSearch()
            else handleSearch()
          }}
          disabled={showClearButton ? isSearching : isSearchDisabled}
        >
          {isSearching ? (
            <Loader2 className={`${filterSearchBarIconClassName} animate-spin`} />
          ) : showClearButton ? (
            <X className={filterSearchBarIconClassName} />
          ) : (
            <Search className={filterSearchBarIconClassName} />
          )}
        </Button>
      </div>
    </div>
  )
}

